var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import apiClient from '@utils/api-client';
// `update_action` values task-15's `#bulk_update` switches on — must match
// `Api::Internal::Buildings::AttachmentsController::UPDATE_ACTION_*` exactly.
const BULK_UPDATE_ACTION_ASSIGN_CATEGORY = 'assign_category';
const BULK_UPDATE_ACTION_VISIBILITY = 'show_on_customer_portal';
const BULK_UPDATE_ACTION_IS_PRIVATE = 'is_private';
/**
 * Fetches every attachment for a building in one request (`select[all]=true`).
 * Filtering/sorting/search happen client-side (see `AttachmentsTable`) — buildings
 * hold well under ~100 attachments, so there is no pagination to manage here.
 */
export function fetchBuildingAttachments(buildingId) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.get(`/api/internal/buildings/${buildingId}/attachments`, { params: { select: { all: true } } });
        return response.data.data;
    });
}
/**
 * Task-14's `update` action — a single-field partial payload (the inline-edit cells never send
 * more than one key), returning the full, server-confirmed row. `apiClient`'s case-converter
 * middleware turns `buildingAttachment` into the `building_attachment` param key Rails expects.
 */
export function updateBuildingAttachment(buildingId, attachmentId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`/api/internal/buildings/${buildingId}/attachments/${attachmentId}`, { buildingAttachment: payload });
        return response.data;
    });
}
/**
 * Task-15's mass-update endpoint, `assign_category` branch (E2E-30). This task's UI always sends
 * the explicit checked-row `ids` — see `useAttachmentMassActions`'s selection-semantics comment for
 * why the endpoint's "absent ids falls back to the current filtered set" mode is never invoked here.
 */
export function bulkUpdateAttachmentCategory(buildingId, ids, attachmentCategoryId) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`/api/internal/buildings/${buildingId}/attachments/bulk_update`, { ids, updateAction: BULK_UPDATE_ACTION_ASSIGN_CATEGORY, attachmentCategoryId });
        return response.data;
    });
}
/**
 * Task-15's mass-update endpoint, `show_on_customer_portal` branch (E2E-31). Unlike the legacy
 * one-way `mass_update`, this accepts an explicit boolean and can un-publish — a genuinely new
 * capability.
 */
export function bulkUpdateAttachmentVisibility(buildingId, ids, showOnCustomerPortal) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`/api/internal/buildings/${buildingId}/attachments/bulk_update`, { ids, updateAction: BULK_UPDATE_ACTION_VISIBILITY, showOnCustomerPortal });
        return response.data;
    });
}
/**
 * Task-23's mass-update endpoint branch (E2E-31) — the bulk counterpart of the per-row `is_private`
 * checkbox (task-17). Two-way, like `bulkUpdateAttachmentVisibility`: can both hide and un-hide a
 * selection from technician view.
 */
export function bulkUpdateAttachmentTechVisibility(buildingId, ids, isPrivate) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`/api/internal/buildings/${buildingId}/attachments/bulk_update`, { ids, updateAction: BULK_UPDATE_ACTION_IS_PRIVATE, isPrivate });
        return response.data;
    });
}
/** Task-15's mass-destroy endpoint (E2E-32). Backgrounds itself past 50 ids server-side. */
export function bulkDestroyAttachments(buildingId, ids) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.delete(`/api/internal/buildings/${buildingId}/attachments/bulk_destroy`, { data: { ids } });
        return response.data;
    });
}
/** Task-14's single-row destroy action (E2E-33). */
export function destroyAttachment(buildingId, attachmentId) {
    return __awaiter(this, void 0, void 0, function* () {
        yield apiClient.delete(`/api/internal/buildings/${buildingId}/attachments/${attachmentId}`);
    });
}
/**
 * Creates a tenant attachment category from the attachments drawer. The endpoint applies the
 * same authorization as Settings > Attachment Categories, so a user without access gets a 403
 * even though the UI hides the action.
 */
export function createAttachmentCategory(payload) {
    return __awaiter(this, void 0, void 0, function* () {
        // camelCase in, snake_case on the wire — `apiClient` runs axios-case-converter, same as every
        // other call in this file (`buildingAttachment`, `updateAction`).
        const response = yield apiClient.post('/api/internal/attachment_categories', {
            attachmentCategory: { name: payload.name, description: payload.description }
        });
        return response.data;
    });
}
